import { Tv } from 'lucide-react';
import SectionHeading from '../components/SectionHeading';
import Reveal from '../components/Reveal';
import Marquee from '../components/Marquee';
import { useLanguage } from '../i18n/LanguageContext';
import { aboutContent } from '../i18n/content/about';

import w9 from '../assets/channel/W9.png';
import ocs from '../assets/channel/OCS.png';
import dazn from '../assets/channel/DAZN.png';
import netflix from '../assets/channel/Netflix.png';
import primeVideo from '../assets/channel/Prime_Video.png';
import m6 from '../assets/channel/M6.png';
import tf1 from '../assets/channel/TF1.png';
import arte from '../assets/channel/Arte.png';
import mbcGroup from '../assets/channel/MBC_Group.png';
import canalPlus from '../assets/channel/Canal+.png';

const REFUND_ICON = <svg xmlns="http://www.w3.org/2000/svg" width="34px" height="34px" viewBox="0 0 24 24"><g fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}><path d="M16 14a1.5 1.5 0 1 0 3 0a1.5 1.5 0 0 0-3 0m2.9-6q.1-.486.1-1a5 5 0 1 0-9.9 1"></path><path d="M7 7.993h9c2.828 0 4.243 0 5.121.88c.879.878.879 2.293.879 5.123v2.001c0 2.83 0 4.245-.879 5.124C20.243 22 18.828 22 16 22h-6c-3.771 0-5.657 0-6.828-1.172S2 17.769 2 13.996v-2c0-3.774 0-5.66 1.172-6.833C4.115 4.22 5.52 4.036 8 4h2"></path></g></svg>;
const SPEED_ICON = <svg xmlns="http://www.w3.org/2000/svg" width="34px" height="34px" viewBox="0 0 24 24"><path fill="currentColor" d="M7 2v11h3v9l7-12h-4l3-8z"></path></svg>;
const INSTALL_ICON = <svg xmlns="http://www.w3.org/2000/svg" width="34px" height="34px" viewBox="0 0 24 24"><path fill="currentColor" d="M13 2.05v2.02c3.95.49 7 3.85 7 7.93c0 3.21-1.92 6-4.72 7.28L13 17v5h5l-1.22-1.22C19.91 19.07 22 15.76 22 12c0-5.18-3.95-9.45-9-9.95M11 2c-1.95.2-3.8.96-5.32 2.21L7.1 5.63A8.2 8.2 0 0 1 11 4zM4.2 5.68C2.96 7.2 2.2 9.05 2 11h2c.19-1.42.75-2.77 1.63-3.9zM6 8v2h3v1H8c-1.1 0-2 .9-2 2v3h5v-2H8v-1h1c1.11 0 2-.89 2-2v-1a2 2 0 0 0-2-2zm6 0v5h3v3h2v-3h1v-2h-1V8h-2v3h-1V8zM2 13c.2 1.95.97 3.8 2.22 5.32l1.42-1.42A8.2 8.2 0 0 1 4 13zm5.11 5.37l-1.43 1.42A10.04 10.04 0 0 0 11 22v-2a8.06 8.06 0 0 1-3.89-1.63"></path></svg>;

const CHANNELS_ROW_1 = [
    { id: 1, image: w9, name: "W9" },
    { id: 2, image: ocs, name: "OCS" },
    { id: 3, image: dazn, name: "DAZN" },
    { id: 4, image: netflix, name: "Netflix" },
    { id: 5, image: primeVideo, name: "Prime Video" },
];

const CHANNELS_ROW_2 = [
    { id: 1, image: m6, name: "M6" },
    { id: 2, image: tf1, name: "TF1" },
    { id: 3, image: arte, name: "Arte" },
    { id: 4, image: mbcGroup, name: "MBC Group" },
    { id: 5, image: canalPlus, name: "Canal+" },
];

const ChannelLogo = (channel) => (
    <div className="bg-white rounded-card-md border border-line px-4 py-2.5 shadow-sm w-28 sm:w-32 flex items-center justify-center h-12 md:h-14">
        <img
            src={channel.image}
            alt={channel.name}
            loading="lazy"
            className="max-w-full max-h-full object-contain opacity-90"
        />
    </div>
);

export default function About(){
    const { lang } = useLanguage();
    const c = aboutContent[lang];
    return (
        <section className="about-section section2 pb-12 sm:pb-16" style={{backgroundColor: 'var(--bg-color)'}}>
            <div className="main-content">
                <Reveal className="mb-10">
                    <SectionHeading eyebrow={c.eyebrow} title={c.title} align="left" />
                </Reveal>

                <div className="grid grid-cols-1 gap-5">
                    {/* Full-width featured tile: catalogue + channel logo showcase */}
                    <Reveal>
                        <div className="bg-surface border border-line rounded-card-lg shadow-lg p-6 sm:p-8 flex flex-col lg:flex-row lg:items-center gap-8">
                            <div className="flex-1 min-w-0">
                                <div className="w-14 h-14 rounded-2xl flex items-center justify-center mb-5 text-white shadow-md" style={{ background: 'linear-gradient(160deg, #3B82F6, #1D4ED8)' }}>
                                    <Tv size={26} strokeWidth={2} />
                                </div>
                                <h3 className="text-2xl sm:text-3xl font-black text-ink mb-3 leading-tight">{c.featuredTitle}</h3>
                                <p className="text-ink-muted leading-relaxed mb-5 max-w-md">{c.featuredBody}</p>
                                <div className="flex flex-wrap gap-2">
                                    {c.tags.map((tag) => (
                                        <span key={tag} className="px-3 py-1.5 rounded-full bg-primary-light text-primary text-xs font-semibold">
                                            {tag}
                                        </span>
                                    ))}
                                </div>
                            </div>

                            <div className="flex-1 min-w-0 flex flex-col gap-3 lg:max-w-[420px]">
                                <Marquee items={CHANNELS_ROW_1} duration={24} renderItem={ChannelLogo} />
                                <Marquee items={CHANNELS_ROW_2} direction="right" duration={26} renderItem={ChannelLogo} />
                            </div>
                        </div>
                    </Reveal>

                    {/* Three equal feature tiles */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                        <Reveal delay={80} className="h-full">
                            <div className="h-full bg-surface border border-line hover:border-primary/40 transition-all duration-300 rounded-card-lg p-6 flex flex-col justify-center">
                                <span className="text-primary mb-3">{REFUND_ICON}</span>
                                <h3 className="text-lg font-bold text-ink mb-2">{c.tiles[0].title}</h3>
                                <p className="text-sm text-ink-muted">{c.tiles[0].body}</p>
                            </div>
                        </Reveal>

                        <Reveal delay={160} className="h-full">
                            <div className="h-full bg-surface border border-line hover:border-primary/40 transition-all duration-300 rounded-card-lg p-6 flex flex-col justify-center">
                                <span className="text-primary mb-3">{INSTALL_ICON}</span>
                                <h3 className="text-lg font-bold text-ink mb-2">{c.tiles[1].title}</h3>
                                <p className="text-sm text-ink-muted">{c.tiles[1].body}</p>
                            </div>
                        </Reveal>

                        <Reveal delay={240} className="h-full">
                            <div className="h-full bg-surface border border-line hover:border-primary/40 transition-all duration-300 rounded-card-lg p-6 flex flex-col justify-center">
                                <span className="text-primary mb-3">{SPEED_ICON}</span>
                                <h3 className="text-lg font-bold text-ink mb-2">{c.tiles[2].title}</h3>
                                <p className="text-sm text-ink-muted">{c.tiles[2].body}</p>
                            </div>
                        </Reveal>
                    </div>
                </div>
            </div>
        </section>
    )
}
